import React from 'react';

export const GRADING_TIER_DISCLAIMER = "Projected return assumes today's prices and PSA business-day turnaround estimates hold for a full year, and that you redeploy capital into a comparable card each cycle (~14 calendar days budgeted between cycles to buy, receive, and resubmit). Actual results will vary — fees, turnaround times, and market prices change frequently and are outside our control.";

const GRADING_TIER_TOOLTIP_TEXT = "Compares eligible PSA service levels for this card. Rows stay in PSA price order, and the highlighted row is the tier with the strongest projected annual return at current prices.";

const TABLE_HEADER_TOOLTIPS = {
  service: "The PSA submission level available for this card based on its PSA 10 market value.",
  fee: "Estimated PSA grading fee per card for that service level.",
  tat: "TAT means estimated PSA turnaround time, shown as the midpoint or placeholder business-day estimate for that tier.",
  submissions: "Estimated number of grading cycles per year after converting PSA business days to a calendar cycle and adding a resubmission buffer.",
  returnPerSubmission: "Estimated gross dollars per submitted card after raw cost and grading fee.",
  projectedReturn: "Estimated annual gross return per card if the same capital is redeployed into comparable submissions each cycle."
};

const PSA_SERVICE_METADATA = {
  "Value": { order: 0, turnaroundBusinessDays: 95 },
  "Standard": { order: 1, turnaroundBusinessDays: 75 },
  "Priority": { order: 2, turnaroundBusinessDays: 55 },
  "Express": { order: 3, turnaroundBusinessDays: 25 }
};

const parseGradingTierEfficiency = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (Array.isArray(value.tiers)) return value.tiers;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parseGradingTierEfficiency(parsed);
    } catch (error) {
      return [];
    }
  }
  return [];
};

const normalizeRows = (rows) => {
  const normalizedRows = rows
    .filter((row) => PSA_SERVICE_METADATA[row.service])
    .filter((row) => row.service !== 'Value' || Number(row.fee) === 39.99)
    .map((row) => {
      const serviceMetadata = PSA_SERVICE_METADATA[row.service];
      const turnaroundDays = serviceMetadata.turnaroundBusinessDays;
      const cyclesPerYear = 365 / (Math.round(turnaroundDays * 1.4) + 14);
      return {
        ...row,
        service_order: serviceMetadata.order,
        turnaround_days: turnaroundDays,
        cycles_per_year: cyclesPerYear,
        projected_return: Math.round(Number(row.return_per_submission) * cyclesPerYear * 100) / 100,
        recommended: false
      };
    })
    .sort((a, b) => {
      if (a.service_order !== b.service_order) return a.service_order - b.service_order;
      return Number(a.fee) - Number(b.fee);
    });

  if (!normalizedRows.some((row) => row.recommended)) {
    const bestVisibleTier = normalizedRows.reduce((bestRow, row) => {
      if (!bestRow) return row;
      return Number(row.projected_return) > Number(bestRow.projected_return) ? row : bestRow;
    }, null);

    if (bestVisibleTier && Number(bestVisibleTier.projected_return) > 0) {
      return normalizedRows.map((row) => ({
        ...row,
        recommended: row.service === bestVisibleTier.service
      }));
    }
  }

  return normalizedRows;
};

const formatCompactCurrency = (value, options = {}) => {
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return 'N/A';
  const absValue = Math.abs(numberValue);
  const maximumFractionDigits = options.forceCents ? 2 : (absValue < 100 ? 2 : 0);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: options.forceCents ? 2 : 0,
    maximumFractionDigits
  }).format(absValue);
  return numberValue < 0 ? `-${formatted}` : formatted;
};

const formatFee = (value) => {
  return formatCompactCurrency(value, { forceCents: true });
};

const formatTurnaroundDays = (value) => {
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return 'N/A';
  return `${Number.isInteger(numberValue) ? numberValue : numberValue.toFixed(1)} business days`;
};

const formatCompactTurnaroundDays = (value) => {
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return 'N/A';
  return `${Number.isInteger(numberValue) ? numberValue : numberValue.toFixed(1)}d`;
};

const GradingTierEfficiencyTable = ({ tiers }) => {
  const rows = normalizeRows(parseGradingTierEfficiency(tiers));
  if (!rows.length) return null;

  const hasRecommendedTier = rows.some(row => row.recommended);

  return (
    <div className="grading-tier-efficiency">
      <h3 className="grading-tier-title section-header-with-info">
        Grading tier efficiency
        <span className="info-tooltip-wrapper" onClick={(event) => event.stopPropagation()}>
          <span className="info-icon" tabIndex={0} aria-label="Grading tier efficiency information">i</span>
          <span className="info-tooltip-text">{GRADING_TIER_TOOLTIP_TEXT}</span>
        </span>
      </h3>
      {!hasRecommendedTier && (
        <div className="grading-tier-warning">
          No service level projects a positive return at current prices.
        </div>
      )}
      <div className="grading-tier-table-wrap">
        <table className="grading-tier-table">
          <thead>
            <tr>
              <th title={TABLE_HEADER_TOOLTIPS.service}>Service</th>
              <th title={TABLE_HEADER_TOOLTIPS.fee}>Fee</th>
              <th title={TABLE_HEADER_TOOLTIPS.tat}>TAT (business days)</th>
              <th title={TABLE_HEADER_TOOLTIPS.submissions}>Subs/yr</th>
              <th title={TABLE_HEADER_TOOLTIPS.returnPerSubmission}>$/sub</th>
              <th title={TABLE_HEADER_TOOLTIPS.projectedReturn}>
                <span className="grading-return-label-full">Projected Return</span>
                <span className="grading-return-label-short">Return</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const returnPerSubmission = Number(row.return_per_submission);
              const projectedReturn = Number(row.projected_return);
              return (
                <tr
                  key={row.service}
                  className={row.recommended ? 'recommended-tier' : undefined}
                >
                  <td>
                    {row.service}
                    {row.recommended && <span className="recommended-check" aria-label="recommended">✓</span>}
                  </td>
                  <td>{formatFee(row.fee)}</td>
                  <td title={formatTurnaroundDays(row.turnaround_days)}>
                    {formatCompactTurnaroundDays(row.turnaround_days)}
                  </td>
                  <td>{Number(row.cycles_per_year).toFixed(1)}</td>
                  <td className={returnPerSubmission < 0 ? 'negative-return' : undefined}>
                    {formatCompactCurrency(returnPerSubmission)}
                  </td>
                  <td className={projectedReturn < 0 ? 'negative-return' : undefined}>
                    {formatCompactCurrency(projectedReturn)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="grading-tier-mobile-list">
        {rows.map((row) => {
          const returnPerSubmission = Number(row.return_per_submission);
          const projectedReturn = Number(row.projected_return);
          return (
            <div
              key={`${row.service}-mobile`}
              className={`grading-tier-mobile-row ${row.recommended ? 'recommended-tier' : ''}`}
            >
              <div className="grading-tier-mobile-main">
                <span className="grading-tier-mobile-service">
                  {row.service}
                  {row.recommended && <span className="recommended-check" aria-label="recommended">✓</span>}
                </span>
                <span className={projectedReturn < 0 ? 'negative-return' : undefined}>
                  {formatCompactCurrency(projectedReturn)}
                </span>
              </div>
              <div className="grading-tier-mobile-details">
                <span>Fee {formatFee(row.fee)}</span>
                <span>TAT {formatTurnaroundDays(row.turnaround_days)}</span>
                <span>{Number(row.cycles_per_year).toFixed(1)} subs/yr</span>
                <span className={returnPerSubmission < 0 ? 'negative-return' : undefined}>
                  {formatCompactCurrency(returnPerSubmission)}/sub
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <p className="grading-tier-disclaimer">{GRADING_TIER_DISCLAIMER}</p>
    </div>
  );
};

export default GradingTierEfficiencyTable;
